import React, { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';

import OngoingLottie from '@/assets/lottie/loading-dot-ongoing.json';
import WaitLottie from '@/assets/lottie/loading-dot-wait.json';
import CustomLottieIcon from '@/components/atom/CustomLottieIcon';
import NurseProfile from '@/components/molecules/nurse-profile';
import QuestionItem from '@/components/molecules/question-list/question-item';
import theme from '@/styles/theme';
import { QuestionStatus } from '@/types/bff/gql-schema';
import { RelatedQnaVo } from '@/types/qna';
import { EQuestionAnsweredStatus } from '@/types/search';

export interface RelatedQuestionItemProps {
  questionCard: RelatedQnaVo;
  onClickRelatedQna: (questionPid: string) => void;
  isLast?: boolean;
  titleAsH2?: boolean;
}

const RelatedQuestionItem: FunctionComponent<RelatedQuestionItemProps> = ({
  titleAsH2 = true,
  questionCard,
  onClickRelatedQna,
  isLast,
}) => {
  const { questionPid } = questionCard.question;

  return (
    <Container isLast={isLast}>
      <QuestionItem
        titleAsH2={titleAsH2}
        isPaidQuestion={false}
        key={questionPid}
        questionCard={questionCard}
        onClickQuestion={() => onClickRelatedQna(questionPid)}
        isLast={isLast}
      >
        <QuestionItem.BottomLeft>
          {questionCard.answeredStatus === EQuestionAnsweredStatus.Answered ? (
            <QuestionBottomAnswerStatus>
              {/* 답변한 의사 프로필/카운트 (최대 5개) */}
              <QuestionDoctorImageWrapper>
                {questionCard.answererProfileImageUrls.slice(0, 5).map((image, key) => (
                  <QuestionDoctorImage imageKey={key} key={`answered-doctor-${key}`}>
                    <NurseProfile url={image} size={28} />
                  </QuestionDoctorImage>
                ))}
              </QuestionDoctorImageWrapper>
              {questionCard.answererCount}개의 답변
            </QuestionBottomAnswerStatus>
          ) : questionCard.question.status !== QuestionStatus.AwaitingBasicAnswer ? (
            // 답변 중 / 답변 확인 중
            // 목록
            <QuestionBottomAnswerStatus
              fontColor={
                questionCard.answeredStatus === EQuestionAnsweredStatus.Answering
                  ? theme.color.primary500
                  : theme.color.blue500
              }
            >
              <CustomLottieIcon
                width={24}
                height={24}
                animationData={
                  questionCard.answeredStatus === EQuestionAnsweredStatus.Answering ? OngoingLottie : WaitLottie
                }
                style={{ marginRight: 8 }}
              />
              {questionCard.answeredStatusText}
            </QuestionBottomAnswerStatus>
          ) : (
            <></>
          )}
        </QuestionItem.BottomLeft>
      </QuestionItem>
    </Container>
  );
};

export default RelatedQuestionItem;

const Container = styled.li<{ isLast?: boolean }>`
  ${({ theme }) => `
  &: hover {
    background: ${theme.color.gray30};
  };
  &: active {
    background: ${theme.color.gray50};
  };
  `};

  ${({ theme, isLast }) => css`
    & > div {
      border-bottom: ${isLast ? 'none' : `1px solid ${theme.color.gray40}`};
    }
  `}
`;

const QuestionBottomAnswerStatus = styled.div<{ fontColor?: string }>`
  display: flex;
  align-items: center;

  ${({ theme, fontColor }) => `
    ${theme.typography.UI14SB};
    color: ${fontColor ?? theme.color.gray700};
  `};

  &:div {
    margin-right: 8px;
  }
`;

const QuestionDoctorImageWrapper = styled.div`
  display: flex;
  position: relative;
  margin-right: 8px;
`;

const QuestionDoctorImage = styled.div<{ imageKey: number }>`
  div {
    border: 2px solid white;
    border-radius: 50%;
  }

  ${({ imageKey }) => `
    margin-left: -${imageKey ? 8 : 0}px;
    z-index: ${5 - imageKey};
  `};
`;
